import { useState, useRef, useEffect, useMemo } from 'react';

import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';

// material-ui
import { useTheme } from '@mui/material/styles';
import {
    Box,
    Chip,
    ClickAwayListener,
    CircularProgress,
    Divider,
    InputAdornment,
    List,
    ListItemButton,
    ListItemIcon,
    ListItemText,
    OutlinedInput,
    Paper,
    Popper,
    Stack,
    Typography,
    IconButton,
    Tooltip
} from '@mui/material';

// third-party
import PerfectScrollbar from 'react-perfect-scrollbar';

// project imports
import MainCard from 'ui-component/cards/MainCard';
import Transitions from 'ui-component/extended/Transitions';
import Controls from 'views/components/controls/Controls';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import SnackbarUtils from 'views/components/SnackbarUtils';

// assets
import { IconLogout, IconSearch, IconSettings } from '@tabler/icons';
import { getCatalogName, getNomeUsuario, logout } from 'services/auth';
import { getCachedCatalogListData, setCachedCatalogListData } from 'services/catalogTokenCache';
import * as usuarioService from 'services/usuarioService';
import { switchCatalogSession } from 'services/usuarioSessionService';

const CATALOG_LIST_MAX_HEIGHT = 240;

function getCatalogLabel(catalog) {
    if (!catalog) {
        return '';
    }
    if (catalog.company && catalog.company.fantasyName) {
        return catalog.company.fantasyName;
    }
    if (catalog.catalogName) {
        return catalog.catalogName.split('_')[0];
    }
    return '';
}

function normalizeCatalogList(data) {
    const catalogs = Array.isArray(data?.catalogs) ? [...data.catalogs] : [];
    const activeName = getCatalogName();
    const defaultCatalog = data?.defaultCatalog;

    if (defaultCatalog?.catalogName) {
        const exists = catalogs.some((c) => c.catalogName === defaultCatalog.catalogName);
        if (!exists) {
            catalogs.unshift({
                catalogName: defaultCatalog.catalogName,
                dbName: defaultCatalog.dbName,
                company: {
                    fantasyName: defaultCatalog.catalogName.split('_')[0]
                }
            });
        }
    }

    if (activeName) {
        const activeExists = catalogs.some((c) => c.catalogName === activeName);
        if (!activeExists) {
            catalogs.unshift({
                catalogName: activeName,
                company: { fantasyName: activeName.split('_')[0] }
            });
        }
    }

    const seen = new Set();
    return catalogs.filter((c) => {
        if (!c?.catalogName || seen.has(c.catalogName)) {
            return false;
        }
        seen.add(c.catalogName);
        return true;
    });
}

function mensagemErroApi(error, fallback) {
    const msg = error?.response?.data?.message;
    if (Array.isArray(msg)) {
        return msg.join(', ');
    }
    if (msg) {
        return msg;
    }
    return fallback;
}

// ==============================|| PROFILE MENU ||============================== //

const ProfileSection = () => {
    const theme = useTheme();
    const customization = useSelector((state) => state.customization);
    const navigate = useNavigate();

    const [search, setSearch] = useState('');
    const [selectedIndex, setSelectedIndex] = useState(-1);
    const [open, setOpen] = useState(false);
    const [catalogs, setCatalogs] = useState(() => {
        const cached = getCachedCatalogListData();
        return cached ? normalizeCatalogList(cached) : [];
    });
    const [loadingCatalogs, setLoadingCatalogs] = useState(false);
    const [switching, setSwitching] = useState(false);

    const anchorRef = useRef(null);
    const catalogsFetchAttemptedRef = useRef(false);

    const handleLogout = async () => {
        logout();
        navigate('/login');
    };

    const handleClose = (event) => {
        if (anchorRef.current && anchorRef.current.contains(event.target)) {
            return;
        }
        setOpen(false);
    };

    const handleToggle = () => {
        setOpen((prevOpen) => !prevOpen);
    };

    const prevOpen = useRef(open);
    useEffect(() => {
        if (prevOpen.current === true && open === false) {
            anchorRef.current.focus();
            setSearch('');
        }

        prevOpen.current = open;
    }, [open]);

    useEffect(() => {
        if (!open) {
            return;
        }

        const cached = getCachedCatalogListData();
        if (cached) {
            setCatalogs(normalizeCatalogList(cached));
            return;
        }

        if (catalogsFetchAttemptedRef.current) {
            return;
        }

        catalogsFetchAttemptedRef.current = true;

        let cancelled = false;

        async function loadCatalogs() {
            setLoadingCatalogs(true);
            try {
                const response = await usuarioService.listCatalogs();
                if (!cancelled && response.data) {
                    setCachedCatalogListData(response.data);
                    setCatalogs(normalizeCatalogList(response.data));
                }
            } catch (error) {
                if (!cancelled) {
                    setCatalogs([]);
                    SnackbarUtils.error(mensagemErroApi(error, 'Erro ao carregar catálogos.'));
                }
            } finally {
                if (!cancelled) {
                    setLoadingCatalogs(false);
                }
            }
        }

        loadCatalogs();

        return () => {
            cancelled = true;
        };
    }, [open]);

    const nomeUsuario = getNomeUsuario();
    const catalogNameRaw = getCatalogName();
    const catalogName = catalogNameRaw ? catalogNameRaw.split('_')[0] : '';

    const activeCatalogName = catalogNameRaw || '';

    const filteredCatalogs = useMemo(() => {
        const term = search.trim().toLowerCase();
        if (!term) {
            return catalogs;
        }
        return catalogs.filter((catalog) => {
            const fantasyName = (catalog.company?.fantasyName || '').toLowerCase();
            const corporate = (catalog.company?.corporateName || '').toLowerCase();
            const nickname = (catalog.company?.nickname || '').toLowerCase();
            const name = (catalog.catalogName || '').toLowerCase();
            return (
                fantasyName.includes(term) ||
                corporate.includes(term) ||
                nickname.includes(term) ||
                name.includes(term)
            );
        });
    }, [catalogs, search]);

    const handleSelectCatalog = async (catalog) => {
        if (!catalog?.catalogName || catalog.catalogName === activeCatalogName || switching) {
            return;
        }

        setSwitching(true);
        try {
            await switchCatalogSession(catalog.catalogName);
            SnackbarUtils.success('Empresa alterado com sucesso.');
            window.location.assign('/');
        } catch (error) {
            SnackbarUtils.error(mensagemErroApi(error, 'Erro ao trocar de empresa.'));
            setSwitching(false);
        }
    };

    const openHelpPage = () => {
        navigate('/documentacao#Inicio.md');
    };

    return (
        <>
            <Controls.DialogSpinner open={switching} />
            <Tooltip title="Ajuda e Documentação">
                <IconButton
                    onClick={openHelpPage}
                    color="primary"
                    aria-label="Ajuda e Documentação"
                >
                    <HelpOutlineIcon />
                </IconButton>
            </Tooltip>
            <Chip
                sx={{
                    height: '48px',
                    alignItems: 'center',
                    borderRadius: '27px',
                    transition: 'all .2s ease-in-out',
                    borderColor: theme.palette.primary.light,
                    backgroundColor: theme.palette.primary.light,
                    '&[aria-controls="menu-list-grow"], &:hover': {
                        borderColor: theme.palette.primary.main,
                        background: `${theme.palette.primary.main}!important`,
                        color: theme.palette.primary.light,
                        '& svg': {
                            stroke: theme.palette.primary.light
                        }
                    },
                    '& .MuiChip-label': {
                        lineHeight: 0
                    }
                }}
                icon={
                    <h3 style={{ paddingLeft: '12px' }}>
                        {catalogName ? `${nomeUsuario} - ${catalogName}` : nomeUsuario}
                    </h3>
                }
                label={
                    <IconSettings stroke={1.5} size="1.5rem" color={theme.palette.primary.main} />
                }
                variant="outlined"
                ref={anchorRef}
                aria-controls={open ? 'menu-list-grow' : undefined}
                aria-haspopup="true"
                onClick={handleToggle}
                color="primary"
            />
            <Popper
                placement="bottom-end"
                open={open}
                anchorEl={anchorRef.current}
                role={undefined}
                transition
                disablePortal
                popperOptions={{
                    modifiers: [
                        {
                            name: 'offset',
                            options: {
                                offset: [0, 14]
                            }
                        }
                    ]
                }}
            >
                {({ TransitionProps }) => (
                    <Transitions in={open} {...TransitionProps}>
                        <Paper>
                            <ClickAwayListener onClickAway={handleClose}>
                                <MainCard border={false} elevation={16} content={false} boxShadow shadow={theme.shadows[16]}>
                                    <Box sx={{ pt: 2, pb: 1, pr: 1, pl: 1 }}>
                                        <Stack>
                                            <Stack direction="row" spacing={0.5} alignItems="center">
                                                <Typography variant="h4">Olá,</Typography>
                                                <Typography component="span" variant="h4" sx={{ fontWeight: 400 }}>
                                                    {catalogName
                                                        ? `${nomeUsuario} - ${catalogName}`
                                                        : nomeUsuario}
                                                </Typography>
                                            </Stack>
                                        </Stack>
                                    </Box>
                                    <Divider />
                                    <Box sx={{ p: 1 }}>
                                        <OutlinedInput
                                            fullWidth
                                            size="small"
                                            placeholder="Pesquisar empresa"
                                            value={search}
                                            onChange={(e) => setSearch(e.target.value)}
                                            startAdornment={
                                                <InputAdornment position="start">
                                                    <IconSearch stroke={1.5} size="1rem" />
                                                </InputAdornment>
                                            }
                                            sx={{ mb: 1 }}
                                        />
                                        {loadingCatalogs ? (
                                            <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                                                <CircularProgress size={24} />
                                            </Box>
                                        ) : filteredCatalogs.length === 0 ? (
                                            <Typography variant="body2" sx={{ px: 1, py: 1, color: 'text.secondary' }}>
                                                Nenhuma empresa encontrado.
                                            </Typography>
                                        ) : (
                                            <PerfectScrollbar style={{ maxHeight: CATALOG_LIST_MAX_HEIGHT }}>
                                                <List
                                                    component="nav"
                                                    dense
                                                    sx={{
                                                        width: '100%',
                                                        maxWidth: 350,
                                                        minWidth: 240,
                                                        '& .MuiListItemButton-root': {
                                                            mt: 0
                                                        }
                                                    }}
                                                >
                                                    {filteredCatalogs.map((catalog) => {
                                                        const isActive =
                                                            catalog.catalogName === activeCatalogName;
                                                        return (
                                                            <ListItemButton
                                                                key={catalog.catalogName}
                                                                selected={isActive}
                                                                disabled={isActive || switching}
                                                                onClick={() => handleSelectCatalog(catalog)}
                                                                sx={{
                                                                    borderRadius: `${customization.borderRadius}px`
                                                                }}
                                                            >
                                                                <ListItemText
                                                                    primary={
                                                                        <Typography variant="body2">
                                                                            {getCatalogLabel(catalog)}
                                                                        </Typography>
                                                                    }
                                                                />
                                                            </ListItemButton>
                                                        );
                                                    })}
                                                </List>
                                            </PerfectScrollbar>
                                        )}
                                    </Box>
                                    <Divider />
                                    <Box sx={{ p: 1 }}>
                                        <List
                                            component="nav"
                                            sx={{
                                                width: '100%',
                                                maxWidth: 350,
                                                minWidth: 200,
                                                '& .MuiListItemButton-root': {
                                                    mt: 0
                                                }
                                            }}
                                        >
                                            <ListItemButton
                                                sx={{ borderRadius: `${customization.borderRadius}px` }}
                                                selected={selectedIndex === 4}
                                                onClick={handleLogout}
                                            >
                                                <ListItemIcon>
                                                    <IconLogout stroke={1.5} size="1.3rem" />
                                                </ListItemIcon>
                                                <ListItemText primary={<Typography variant="body2">Logout</Typography>} />
                                            </ListItemButton>
                                        </List>
                                    </Box>
                                </MainCard>
                            </ClickAwayListener>
                        </Paper>
                    </Transitions>
                )}
            </Popper>
        </>
    );
};

export default ProfileSection;
